import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Activity, ArrowRight, FileText, Info, Menu, Moon, Sun, X } from "lucide-react";

import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useTheme } from "../contexts/ThemeContext";
import logomark from "../img/logomark.svg";
import { ARROW_NUDGE, INK_BUTTON } from "../constants/linkStyle";

import SignIn from "./signIn";
import QuickstartDialog from "./app/QuickstartDialog";

/**
 * The global header.
 *
 * Two shapes, one component:
 *
 * - Marketing (`navClass="contentpage"`) is a floating white card inset to the
 *   same 1440/40px shell the page sections use, so its edges line up with the
 *   boxes below it.
 * - The app (`navClass="zatomap"`) is a full-width bar with a bottom hairline.
 *   It stays 56px tall because `pt-14` and `calc(100vh-3.5rem)` are baked into
 *   AppResultPage, AppConsolePage and workspaceLayout.js — the comp says 60px,
 *   and 4px is not worth breaking three app layouts over.
 *
 * The header used to go transparent over hero videos and invert the logo to
 * white. The redesign is an opaque card on every page, so that scroll listener
 * is gone.
 */

/* Three entries, each pointing at a page that exists. How it works is a
   section of Features (/features#how-it-works), not a page of its own, so it
   has no entry: a second label for one page reads like a broken menu. The
   footer links to the section. */
const MENU = [
  { label: "Features", to: "/features" },
  { label: "About", to: "/about" },
  { label: "Articles", to: "/articles" },
];

const MONO = "font-mono text-[10.5px] uppercase tracking-[0.133em] transition-colors";
const MOBILE_ROW =
  "flex items-center gap-3 border-b border-zp-ink/[0.09] px-2 py-4 text-left font-mono text-xs uppercase tracking-[0.133em] text-zp-body";

function Nav(props) {
  const location = useLocation();
  const { theme, toggleTheme, isDark, setTheme } = useTheme();
  const isApp = props.navClass === "zatomap";
  /* Inside the app, the map is a drill-down off the result — offer the way back. */
  const isMapView = location.pathname === "/app/map";

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGuide, setOpenGuide] = useState(false);

  /* Dark mode is only allowed on the app surface. Every other page is locked to
     light, so flip the theme back as soon as the user leaves. */
  useEffect(() => {
    if (!isApp && theme !== "light") {
      setTheme("light");
    }
  }, [isApp, theme, setTheme]);

  const handleOpenGuide = () => setOpenGuide(true);
  const isActive = (to) => location.pathname === to;

  const logo = (
    <Link to="/" className="flex shrink-0 items-center gap-[11px]">
      <img src={logomark} className="h-[22px] w-[22px]" alt="" aria-hidden="true" />
      <span className="font-heading text-[21px] font-semibold -tracking-[0.03em] text-zp-ink">
        zatoplan
      </span>
    </Link>
  );

  const desktopMenu = isApp ? (
    <nav className="hidden items-center gap-7 md:flex">
      {isMapView && (
        <Link to="/app" className={`${MONO} flex items-center gap-1.5 text-zp-ink`}>
          <Activity className="h-3.5 w-3.5" />
          Result
        </Link>
      )}
      <button
        type="button"
        onClick={handleOpenGuide}
        className={`${MONO} flex items-center gap-1.5 text-zp-slate ease-in hover:text-zp-link active:text-zp-link`}
      >
        <Info className="h-3.5 w-3.5" />
        Quickstart
      </button>
      <a
        href="/docs"
        target="_blank"
        rel="noopener noreferrer"
        className={`${MONO} flex items-center gap-1.5 text-zp-slate ease-in hover:text-zp-link active:text-zp-link`}
      >
        <FileText className="h-3.5 w-3.5" />
        Docs
      </a>
    </nav>
  ) : (
    <nav className="hidden items-center gap-[30px] md:flex">
      {MENU.map(({ label, to }) => (
        <Link
          key={to}
          to={to}
          className={`${MONO} ${
            isActive(to) ? "text-zp-ink" : "text-zp-slate ease-in hover:text-zp-link active:text-zp-link"
          }`}
        >
          {label}
        </Link>
      ))}
    </nav>
  );

  const actions = (
    <div className="flex shrink-0 items-center gap-4 sm:gap-[18px]">
      {isApp && (
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="hidden h-8 w-8 md:inline-flex"
          aria-label="Toggle theme"
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
      )}

      <div className="hidden md:block">
        <SignIn variant="minimal" />
      </div>

      {!isApp && (
        <Link
          to="/app"
          className={`${MONO} hidden items-center gap-2 rounded-md bg-zp-ink px-4 py-[11px] text-white ${INK_BUTTON} sm:inline-flex`}
        >
          Launch the map
          <ArrowRight className={`h-[13px] w-[13px] ${ARROW_NUDGE}`} />
        </Link>
      )}

      {/* Mobile menu */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            id="mobilemenu"
            className="md:hidden"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-full bg-white">
          <nav className="mt-10 flex flex-col">
            {!isApp &&
              MENU.map(({ label, to }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className={`${MOBILE_ROW} ${isActive(to) ? "text-zp-ink" : ""}`}
                >
                  {label}
                </Link>
              ))}

            {isApp && (
              <>
                {isMapView && (
                  <Link
                    to="/app"
                    onClick={() => setMobileOpen(false)}
                    className={MOBILE_ROW}
                  >
                    <Activity className="h-4 w-4" />
                    Result
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    handleOpenGuide();
                    setMobileOpen(false);
                  }}
                  className={MOBILE_ROW}
                >
                  <Info className="h-4 w-4" />
                  Quickstart
                </button>
                <a
                  href="/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={MOBILE_ROW}
                >
                  <FileText className="h-4 w-4" />
                  Docs
                </a>
                <button type="button" onClick={toggleTheme} className={MOBILE_ROW}>
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {isDark ? "Light mode" : "Dark mode"}
                </button>
              </>
            )}

            {!isApp && (
              <Link
                to="/app"
                onClick={() => setMobileOpen(false)}
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-md bg-zp-ink px-4 py-3.5 font-mono text-[11px] uppercase tracking-[0.133em] text-white ${INK_BUTTON}`}
              >
                Launch the map
                <ArrowRight className={`h-[13px] w-[13px] ${ARROW_NUDGE}`} />
              </Link>
            )}

            <div className="mt-6 px-2">
              <SignIn />
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );

  return (
    <>
      <header
        id="navmain"
        className={`fixed left-0 right-0 top-0 z-50 ${props.navClass}`}
      >
        {isApp ? (
          <div className="flex h-14 items-center justify-between gap-6 border-b border-zp-ink/[0.08] bg-white px-5 sm:px-6">
            {logo}
            {desktopMenu}
            {actions}
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[1440px] px-5 pt-4 sm:px-10 sm:pt-6">
            <div className="flex h-[68px] items-center justify-between gap-6 rounded-bl-xl rounded-br-xl rounded-xl bg-white px-4 shadow-sm sm:px-[26px]">
              {logo}
              {desktopMenu}
              {actions}
            </div>
          </div>
        )}
      </header>

      <QuickstartDialog open={openGuide} onOpenChange={setOpenGuide} />
    </>
  );
}

export default Nav;
